import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Stack,
  CircularProgress,
  Paper,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Tooltip,
  IconButton
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import CategoryIcon from '@mui/icons-material/Category';
import Images from '../../utils/Images';
import { CategoryFilterChip } from '../../components/FilterChip';
import axios from 'axios';
import { API } from '../../utils/Api';
import { ToggleOffOutlined, ToggleOnOutlined } from '@mui/icons-material';

function Products() {
  const token = localStorage.getItem('token');
  const [selectedChipIndex, setSelectedChipIndex] = useState(null);
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recipesMap, setRecipesMap] = useState({});
  const [stockMap, setStockMap] = useState({});
  const [outletData] = useState(JSON.parse(localStorage.getItem('outletData')));
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [addonAllocations, setAddonAllocations] = useState([]);
  const [addonToggleLoading, setAddonToggleLoading] = useState(null);

  const isOutletExcluded = (excludedOutlets = []) => {
    if (!Array.isArray(excludedOutlets)) return false;

    return excludedOutlets.some((outlet) => {
      if (typeof outlet === 'string') {
        return outlet === outletData?._id;
      }

      return outlet?._id === outletData?._id;
    });
  };

  const flattenCategoriesToProducts = (categoryList) => {
    if (!Array.isArray(categoryList)) return [];

    return categoryList.flatMap((cat) =>
      (cat.products || []).map((prod) => ({
        ...prod,
        category: {
          _id: cat._id,
          name: cat.name,
          description: cat.description
        }
      }))
    );
  };

  const filterProducts = (searchText = '', categoryId = null) => {
    const search = (searchText || '').toLowerCase();
    let filtered = allProducts;

    if (categoryId) {
      filtered = filtered.filter((p) => p.category?._id === categoryId);
    }

    if (search) {
      filtered = filtered.filter((p) => {
        return (
          p.name?.toLowerCase().includes(search) ||
          p.description?.toLowerCase().includes(search) ||
          p.keywords?.toLowerCase().includes(search)
        );
      });
    }

    setProducts(filtered);
  };

  const handleChipClick = (categoryId, index) => {
    if (index === selectedChipIndex) {
      setSelectedChipIndex(null);
      filterProducts(searchQuery, null);
    } else {
      setSelectedChipIndex(index);
      filterProducts(searchQuery, categoryId);
    }
  };

  const handleSearch = (searchText) => {
    setSearchQuery(searchText);

    let categoryId = null;
    if (selectedChipIndex !== null && categories[selectedChipIndex]) {
      categoryId = categories[selectedChipIndex]._id;
    }

    filterProducts(searchText, categoryId);
  };

  const getCategoriesProductsList = async () => {
    if (!outletData?._id) return;

    try {
      setLoading(true);

      const response = await axios.get(
        `${API.BASE_URL}product/getProductsWithAddons`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: {
            outletId: outletData._id
          }
        }
      );

      const fetchedCategories = response?.data?.data?.categories || [];
      const flattened = flattenCategoriesToProducts(fetchedCategories);

      setCategories(fetchedCategories);
      setAllProducts(flattened);
      setProducts(flattened);
    } catch (error) {
      console.error('Error fetching products with addons:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIngredients = async () => {
    try {
      const response = await axios.get(`${API.BASE_URL}recipe`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const recieps = response?.data?.data?.recieps || [];
      const map = {};

      recieps.forEach((recipe) => {
        const productId = recipe?.product?._id;
        if (productId) {
          map[productId] = recipe.rawMaterials || [];
        }
      });

      setRecipesMap(map);
    } catch (error) {
      console.error('Error fetching ingredients/recipes:', error);
    }
  };

  const getRawMaterialsStock = async () => {
    try {
      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            outletId: outletData._id,
            limit: 100000
          }
        }
      );

      const totalRecords = response?.data?.data?.totalRecords || [];
      const map = {};

      totalRecords.forEach((item) => {
        const rmId = item?.rawMaterial?._id;

        if (rmId) {
          map[rmId] = {
            stock: item.currentStock,
            stockInfo: item.stockInfo
          };
        }
      });

      setStockMap(map);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getAddonsAllocation = async () => {
    try {
      const response = await axios.get(
        `${API.BASE_URL}addon-product-allocation`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            limit: 100000
          }
        }
      );

      setAddonAllocations(response?.data?.data?.data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getProductAddonAllocations = () => {
    if (!selectedProduct?._id) return [];

    return addonAllocations.filter((allocation) => {
      const productId =
        typeof allocation.product === 'object'
          ? allocation.product?._id
          : allocation.product;

      return productId === selectedProduct._id;
    });
  };

  const getId = (item) => {
    if (!item) return null;
    if (typeof item === "string") return item;
    return item?._id || item?.value || null;
  };

  const normalizeOutletIds = (outlets = []) => {
    if (!Array.isArray(outlets)) return [];

    return outlets
      .map((outlet) => getId(outlet))
      .filter(Boolean);
  };

  const normalizeExcludedValues = (excludedValues = []) => {
    if (!Array.isArray(excludedValues)) return [];

    return excludedValues
      .map((value) => getId(value))
      .filter(Boolean);
  };

  // const handleAddonValueOutletToggle = async ({
  //   allocation,
  //   addonValue,
  //   currentlyExcluded
  // }) => {
  //   try {
  //     const outletId = outletData?._id;
  //     if (!outletId || !allocation?._id || !addonValue?._id) return;

  //     const rootExcludedOutlets = normalizeOutletIds(allocation.excludedOutlets);
  //     const rootExcludedValues = normalizeExcludedValues(allocation.excludedValues);

  //     const payload = {
  //       _id: allocation._id,

  //       product:
  //         typeof allocation.product === 'object'
  //           ? allocation.product?._id
  //           : allocation.product,

  //       addon:
  //         typeof allocation.addon === 'object'
  //           ? allocation.addon?._id
  //           : allocation.addon,

  //       status: allocation.status || 'ACTIVE',

  //       // ✅ excludedValues → [] if empty
  //       excludedValues: rootExcludedValues.length > 0 ? rootExcludedValues : [],

  //       // ✅ excludedOutlets → null if empty
  //       excludedOutlets: rootExcludedOutlets.length > 0 ? rootExcludedOutlets : null,

  //       values: allocation.values.map((valueItem) => {
  //         let excludedOutlets = normalizeOutletIds(valueItem.excludedOutlets);

  //         if (valueItem._id === addonValue._id) {
  //           if (currentlyExcluded) {
  //             excludedOutlets = excludedOutlets.filter((id) => id !== outletId);
  //           } else if (!excludedOutlets.includes(outletId)) {
  //             excludedOutlets.push(outletId);
  //           }
  //         }

  //         return {
  //           _id: valueItem._id,
  //           value:
  //             typeof valueItem.value === 'object'
  //               ? valueItem.value._id
  //               : valueItem.value,

  //           // ✅ FIX: send null instead of []
  //           excludedOutlets:
  //             excludedOutlets.length > 0 ? excludedOutlets : null,

  //           price: Number(valueItem.price || 0),
  //           subLabel: valueItem.subLabel || null,
  //           status: valueItem.status || 'ACTIVE',
  //           isDefault: valueItem.isDefault || false
  //         };
  //       })
  //     };

  //     await axios.put(`${API.BASE_URL}addon-product-allocation`, payload, {
  //       headers: {
  //         Authorization: `Bearer ${token}`
  //       }
  //     });

  //     await getAddonsAllocation();
  //     await getCategoriesProductsList();
  //   } catch (error) {
  //     console.error('Error updating addon outlet status:', error);
  //   }
  // };

  const handleAddonValueOutletToggle = async ({
    allocation,
    addonValue,
    currentlyExcluded
  }) => {
    const loadingKey = `${allocation?._id}-${addonValue?._id}`;

    try {
      const outletId = outletData?._id;
      if (!outletId || !allocation?._id || !addonValue?._id) return;

      // prevent double / wrong touch while same toggle is running
      if (addonToggleLoading) return;

      setAddonToggleLoading(loadingKey);

      const rootExcludedOutlets = normalizeOutletIds(allocation.excludedOutlets);
      const rootExcludedValues = normalizeExcludedValues(allocation.excludedValues);

      const payload = {
        _id: allocation._id,

        product:
          typeof allocation.product === 'object'
            ? allocation.product?._id
            : allocation.product,

        addon:
          typeof allocation.addon === 'object'
            ? allocation.addon?._id
            : allocation.addon,

        status: allocation.status || 'ACTIVE',

        excludedValues: rootExcludedValues.length > 0 ? rootExcludedValues : [],

        excludedOutlets:
          rootExcludedOutlets.length > 0 ? rootExcludedOutlets : null,

        values: allocation.values.map((valueItem) => {
          let excludedOutlets = normalizeOutletIds(valueItem.excludedOutlets);

          if (valueItem._id === addonValue._id) {
            if (currentlyExcluded) {
              excludedOutlets = excludedOutlets.filter((id) => id !== outletId);
            } else if (!excludedOutlets.includes(outletId)) {
              excludedOutlets.push(outletId);
            }
          }

          return {
            _id: valueItem._id,
            value:
              typeof valueItem.value === 'object'
                ? valueItem.value._id
                : valueItem.value,

            excludedOutlets:
              excludedOutlets.length > 0 ? excludedOutlets : null,

            price: Number(valueItem.price || 0),
            subLabel: valueItem.subLabel || null,
            status: valueItem.status || 'ACTIVE',
            isDefault: valueItem.isDefault || false
          };
        })
      };

      await axios.put(`${API.BASE_URL}addon-product-allocation`, payload, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      await getAddonsAllocation();
      await getCategoriesProductsList();
    } catch (error) {
      console.error('Error updating addon outlet status:', error);
    } finally {
      setAddonToggleLoading(null);
    }
  };

  const visibleAddons = useMemo(() => {
    const allocations = getProductAddonAllocations();

    return allocations
      .filter((allocation) => {
        return !isOutletExcluded(allocation?.excludedOutlets || []);
      })
      .map((allocation) => {
        const values = Array.isArray(allocation.values)
          ? allocation.values.filter((value) => {
            const valueObj = value?.value;

            return (
              !valueObj?.iconInfo &&
              (valueObj?.status !== 'INACTIVE' || value?.isDefault)
            );
          })
          : [];

        return {
          ...allocation,
          title: allocation?.addon?.title,
          badgeImage: allocation?.addon?.badgeImage,
          isOptional: allocation?.addon?.isOptional,
          values
        };
      })
      .filter((allocation) => allocation.values.length > 0);
  }, [addonAllocations, selectedProduct, outletData?._id]);

  const ingredients = useMemo(() => {
    if (!selectedProduct?._id) return [];
    return recipesMap[selectedProduct._id] || [];
  }, [recipesMap, selectedProduct]);

  const getStockColor = (stock) => {
    if (stock > 5) return '#2E7D32';
    if (stock > 0) return '#ED6C02';
    return '#D32F2F';
  };

  useEffect(() => {
    getCategoriesProductsList();
    getIngredients();
    getRawMaterialsStock();
    getAddonsAllocation();
  }, []);

  return (
    <Box sx={{ backgroundColor: '#F6F6F6', minHeight: '100vh' }}>
      <Grid container className="flex-section">
        <Grid
          item
          md={7}
          className="flex-no-scroll"
          sx={{
            paddingTop: '110px',
            height: '100vh',
            overflow: 'hidden'
          }}
        >
          <Box
            className="custom-scrollbar"
            sx={{
              height: '100%',
              overflow: 'auto',
              px: 4,
              pb: 4
            }}
          >
            <Paper
              elevation={0}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                py: 2,
                backgroundColor: '#F6F6F6'
              }}
            >
              <TextField
                fullWidth
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: '14px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '14px'
                  }
                }}
                placeholder="Search products, keywords or description"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />

              <Box
                className="custom-scrollbar"
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  pt: 2,
                  pb: 0.5,
                  justifyContent: 'center'
                }}
              >
                {categories.map((category, index) => (
                  <Box key={category._id} sx={{ flex: '0 0 auto' }}>
                    <CategoryFilterChip
                      filterText={category.name}
                      isSelected={selectedChipIndex === index}
                      onClick={() => handleChipClick(category._id, index)}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>

            {loading ? (
              <Box sx={{ textAlign: 'center', mt: 8 }}>
                <CircularProgress sx={{ color: '#000', mb: 2 }} />
                <Typography>Fetching Products...</Typography>
              </Box>
            ) : products.length > 0 ? (
              <Grid container spacing={2.5} sx={{ mt: 1 }}>
                {products.map((product) => {
                  const isSelected = selectedProduct?._id === product._id;

                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
                      <Paper
                        elevation={0}
                        onClick={() => setSelectedProduct(product)}
                        className="cursor-p"
                        sx={{
                          height: '100%',
                          p: 2,
                          borderRadius: '18px',
                          textAlign: 'center',
                          position: 'relative',
                          border: isSelected
                            ? '2px solid #00A4E2'
                            : '1px solid #E8E8E8',
                          backgroundColor: product.isExhausted
                            ? '#FFF1F1'
                            : '#fff',
                          transition: '0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: '0 10px 24px rgba(0,0,0,0.08)'
                          }
                        }}
                      >
                        {product.isExhausted && (
                          <Tooltip title="Product exhausted">
                            <ReportProblemIcon
                              sx={{
                                position: 'absolute',
                                top: 10,
                                left: 10,
                                color: '#D32F2F'
                              }}
                            />
                          </Tooltip>
                        )}

                        <Box
                          sx={{
                            height: 120,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1.5
                          }}
                        >
                          <img
                            src={product.displayImageUrl || product.displayImage}
                            alt={product.name}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '115px',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = Images.product;
                            }}
                          />
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 600,
                            lineHeight: 1.3,
                            minHeight: 40,

                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {product.name}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography sx={{ textAlign: 'center', fontSize: 16, mt: 4 }}>
                No products available.
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item md={5}>
          <Box
            sx={{
              height: '100vh',
              pt: '110px',
              px: 3,
              pb: 3,
              backgroundColor: '#fff',
              borderLeft: '1px solid #EAEAEA',
              position: 'sticky',
              top: 0
            }}
          >
            {selectedProduct ? (
              <Box
                className="custom-scrollbar"
                sx={{
                  height: '100%',
                  overflow: 'auto',
                  pr: 1
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 5,
                    backgroundColor: '#fff',
                    pb: 2
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      variant="rounded"
                      src={
                        selectedProduct.displayImageUrl ||
                        selectedProduct.displayImage ||
                        Images.product
                      }
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '16px',
                        backgroundColor: '#F6F6F6'
                      }}
                    />

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: 20,
                          fontWeight: 700,
                          lineHeight: 1.25
                        }}
                      >
                        {selectedProduct.name}
                      </Typography>

                      <Typography sx={{ fontSize: 13, color: '#777', mt: 0.5 }}>
                        {selectedProduct.category?.name || '-'}
                      </Typography>
                    </Box>
                  </Stack>

                  {selectedProduct.isExhausted && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.2,
                        borderRadius: '12px',
                        backgroundColor: '#FFF1F1',
                        border: '1px solid #F5B5B5',
                        color: '#D32F2F',
                        fontSize: 14,
                        fontWeight: 600
                      }}
                    >
                      Product is exhausted
                    </Box>
                  )}

                  <Grid container spacing={1.2} sx={{ mt: 2 }}>
                    <Grid item xs={6}>
                      <InfoCard
                        icon={<AccessTimeIcon />}
                        label="Making Time"
                        value={`${selectedProduct.makingTime || 0} min`}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <InfoCard
                        icon={<CategoryIcon />}
                        label="Category"
                        value={selectedProduct.category?.name || '-'}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                {selectedProduct.description && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mt: 2,
                      borderRadius: '16px',
                      backgroundColor: '#F8F8F8'
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: '#777', mb: 0.5 }}>
                      Description
                    </Typography>
                    <Typography sx={{ fontSize: 14.5, lineHeight: 1.5 }}>
                      {selectedProduct.description}
                    </Typography>
                  </Paper>
                )}

                <AccordionBlock
                  id="addons"
                  expanded={expandedAccordion === 'addons'}
                  onChange={() =>
                    setExpandedAccordion(expandedAccordion === 'addons' ? null : 'addons')
                  }
                  title="Addons"
                  icon={<RestaurantMenuIcon />}
                  count={visibleAddons.length}
                >
                  {visibleAddons.length > 0 ? (
                    <Stack spacing={1.5}>
                      {visibleAddons.map((addon) => (
                        <Paper
                          key={addon._id}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: '16px',
                            border: '1px solid #EEEEEE',
                            backgroundColor: '#fff'
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ mb: 1 }}
                          >
                            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                              {addon?.addon?.title || addon?.title || '-'}
                            </Typography>

                            {!addon?.addon?.isOptional && (
                              <Chip
                                size="small"
                                label="Required"
                                sx={{
                                  height: 22,
                                  backgroundColor: '#FFF1F1',
                                  color: '#D32F2F',
                                  fontSize: 11,
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Stack>

                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1
                            }}
                          >
                            {(addon.values || []).map((addonValue) => {
                              const isExcluded = isOutletExcluded(
                                addonValue?.excludedOutlets || []
                              );

                              return (
                                <Paper
                                  key={addonValue?._id}
                                  elevation={0}
                                  sx={{
                                    width: '100%',
                                    p: 1.2,
                                    borderRadius: '14px',
                                    border: isExcluded
                                      ? '1px solid #ffcdd2'
                                      : '1px solid #c8e6c9',
                                    backgroundColor: isExcluded ? '#fff5f5' : '#f7fff8',
                                    transition: '0.2s ease'
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <Box>
                                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                                        {addonValue?.value?.value ||
                                          addonValue?.value ||
                                          addonValue?.subLabel ||
                                          '-'}
                                      </Typography>

                                      <Typography
                                        sx={{
                                          fontSize: 12,
                                          color: isExcluded ? '#D32F2F' : '#2E7D32',
                                          fontWeight: 600
                                        }}
                                      >
                                        {isExcluded
                                          ? 'Excluded from outlet'
                                          : 'Included in outlet'}
                                      </Typography>
                                    </Box>

                                    <IconButton
                                      disabled={Boolean(addonToggleLoading)}
                                      onClick={() =>
                                        handleAddonValueOutletToggle({
                                          allocation: addon,
                                          addonValue,
                                          currentlyExcluded: isExcluded
                                        })
                                      }
                                      sx={{
                                        p: 0.5,
                                        transition: '0.2s',
                                        opacity: addonToggleLoading ? 0.6 : 1,
                                        '&:hover': {
                                          transform: addonToggleLoading ? 'none' : 'scale(1.1)'
                                        }
                                      }}
                                    >
                                      {addonToggleLoading === `${addon?._id}-${addonValue?._id}` ? (
                                        <CircularProgress size={28} sx={{ color: '#000' }} />
                                      ) : isExcluded ? (
                                        <ToggleOffOutlined sx={{ fontSize: 36, color: '#D32F2F' }} />
                                      ) : (
                                        <ToggleOnOutlined sx={{ fontSize: 36, color: '#2E7D32' }} />
                                      )}
                                    </IconButton>
                                  </Stack>
                                </Paper>
                              );
                            })}
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <EmptyText text="No addons included for this product." />
                  )}
                </AccordionBlock>

                <AccordionBlock
                  id="ingredients"
                  expanded={expandedAccordion === 'ingredients'}
                  onChange={() =>
                    setExpandedAccordion(
                      expandedAccordion === 'ingredients' ? null : 'ingredients'
                    )
                  }
                  title="Ingredients"
                  icon={<Inventory2Icon />}
                  count={ingredients.length}
                >
                  {ingredients.length > 0 ? (
                    <Stack spacing={1}>
                      {ingredients.map((item) => {
                        const rm = item.rawMaterial || {};
                        const stockData = stockMap[rm._id];
                        const stock = Number(stockData?.stock || 0);

                        return (
                          <Paper
                            key={item._id}
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: '16px',
                              border: '1px solid #EEEEEE',
                              backgroundColor: '#fff'
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {rm.name || 'Unnamed Raw Material'}
                                </Typography>

                                <Typography sx={{ fontSize: 13, color: '#777' }}>
                                  Required: <b>{item.quantity}</b>
                                  {rm.unit ? ` ${rm.unit}` : ''}
                                </Typography>

                                {item.isImportant && (
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      color: '#D32F2F',
                                      mt: 0.3
                                    }}
                                  >
                                    Important ingredient
                                  </Typography>
                                )}
                              </Box>

                              <Box sx={{ textAlign: 'right', flex: '0 0 auto' }}>
                                <Chip
                                  size="small"
                                  label={`${stock.toFixed(2)} ${rm.unit || ''}`}
                                  sx={{
                                    backgroundColor: `${getStockColor(stock)}14`,
                                    color: getStockColor(stock),
                                    fontWeight: 700
                                  }}
                                />

                                {stockData?.stockInfo && (
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      color: '#777',
                                      mt: 0.5,
                                      maxWidth: 120
                                    }}
                                  >
                                    {stockData.stockInfo}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  ) : (
                    <EmptyText text="No ingredients added for this product." />
                  )}
                </AccordionBlock>
              </Box>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  flexDirection: 'column'
                }}
              >
                <img
                  src={Images.noProductSelected}
                  alt="product-not-selected"
                  style={{ maxWidth: '75%', marginBottom: 20 }}
                />
                <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                  Select a product
                </Typography>
                <Typography sx={{ fontSize: 14, color: '#777', mt: 1 }}>
                  Product details, addons and ingredients will appear here.
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

const InfoCard = ({ icon, label, value }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.3,
        borderRadius: '14px',
        backgroundColor: '#F8F8F8',
        height: '100%'
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '10px',
            backgroundColor: '#E8F7FD',
            color: '#0076A8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': {
              fontSize: 18
            }
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, color: '#777' }}>{label}</Typography>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

const AccordionBlock = ({ title, icon, count, children, expanded, onChange }) => {
  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disableGutters
      elevation={0}
      sx={{
        mt: 2,
        borderRadius: '18px !important',
        border: '1px solid #EEEEEE',
        overflow: 'hidden',
        '&:before': {
          display: 'none'
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 54,
          backgroundColor: expanded ? '#F1FAFF' : '#FAFAFA',
          '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: 1
          }
        }}
      >
        <Box
          sx={{
            color: '#0076A8',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {icon}
        </Box>

        <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>
          {title}
        </Typography>

        <Chip
          size="small"
          label={count}
          sx={{
            height: 22,
            backgroundColor: '#E8F7FD',
            color: '#0076A8',
            fontWeight: 700
          }}
        />
      </AccordionSummary>

      <Divider />

      <AccordionDetails
        className="custom-scrollbar"
        sx={{
          p: 1.5,
          maxHeight: 320,
          overflow: 'auto',
          backgroundColor: '#FCFCFC'
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

const EmptyText = ({ text }) => {
  return (
    <Typography
      sx={{
        fontSize: 14,
        color: '#777',
        backgroundColor: '#fff',
        borderRadius: '14px',
        p: 1.5,
        border: '1px dashed #DDD'
      }}
    >
      {text}
    </Typography>
  );
};

export default Products;